import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  clearOAuthTransientKeys,
  clearReauthAttempts,
  clearStarfleetSessionExpired,
  clearStoredStarfleetUserInfo,
  exchangeAuthorizationCode,
  fetchStarfleetUserInfo,
  getStarfleetTokenEndpoint,
  getStarfleetUserInfoEndpoint,
  idTokenHasDisplayName,
  STARFLEET_CODE_VERIFIER_KEY,
  STARFLEET_OAUTH_STATE_KEY,
  writeStoredStarfleetIdToken,
  writeStoredStarfleetUserInfo,
} from '../../utils/starfleetAuth';
import { loadPublicAppConfig } from '../../utils/loadPublicAppConfig';
import { LoadingScreen } from '../../components';
import './index.css';

/**
 * OAuth redirect URI must match the value sent to /authorize (origin + path).
 */
function getRedirectUri(config) {
  const raw = config?.oauthRedirectPath || '/oauth/callback';
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return `${window.location.origin}${path}`;
}

export default function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // null while the exchange is in flight (and on the happy path, which ends in a
  // redirect to '/'), so the silent-login round-trip just shows a neutral
  // spinner — no "Completing sign-in…" copy. Only set when something actually
  // goes wrong, at which point we render a themed error panel.
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const abort = new AbortController();

    async function run() {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const oauthError = searchParams.get('error');
      const oauthErrorDescription = searchParams.get('error_description');

      if (oauthError) {
        clearOAuthTransientKeys();
        setError(oauthErrorDescription || oauthError || 'Sign-in was cancelled.');
        return;
      }

      if (!code || !state) {
        clearOAuthTransientKeys();
        setError('Missing authorization response. Return to the app and try again.');
        return;
      }

      const expectedState = sessionStorage.getItem(STARFLEET_OAUTH_STATE_KEY);
      const codeVerifier = sessionStorage.getItem(STARFLEET_CODE_VERIFIER_KEY);

      if (!expectedState || state !== expectedState || !codeVerifier) {
        clearOAuthTransientKeys();
        setError('Invalid or expired sign-in session. Please start again from the app.');
        return;
      }

      const config = await loadPublicAppConfig();
      if (cancelled) return;

      const clientId =
        (process.env.REACT_APP_STARFLEET_CLIENT_ID || '').trim() ||
        (config?.starfleetClientId || '').trim();
      const loginOrigin = (config?.starfleetLoginOrigin || '').trim();

      if (!clientId || !loginOrigin) {
        clearOAuthTransientKeys();
        setError('Starfleet client is not configured. Check public config and try again.');
        return;
      }

      const redirectUri = getRedirectUri(config);
      const tokenEndpoint = getStarfleetTokenEndpoint(loginOrigin);

      try {
        const tokens = await exchangeAuthorizationCode({
          tokenEndpoint,
          clientId,
          code,
          redirectUri,
          codeVerifier,
          signal: abort.signal,
        });

        if (cancelled) return;

        const idToken = tokens.id_token;
        if (!idToken) {
          clearOAuthTransientKeys();
          setError('Sign-in succeeded but no identity token was returned.');
          return;
        }

        writeStoredStarfleetIdToken(idToken);
        // We have a live session again, so the re-auth budget and the expiry
        // marker both reset — the next expiry gets a fresh set of attempts.
        clearReauthAttempts();
        clearStarfleetSessionExpired();
        // Any name cached here belongs to a previous sign-in.
        clearStoredStarfleetUserInfo();

        // Starfleet's id_token doesn't always carry a name, which is why the
        // header can fall back to showing a raw email address. When it doesn't,
        // ask /userinfo — the `profile` scope is already requested, so the
        // claims are ours to read.
        //
        // Skipped entirely when the token does name the user, so deployments
        // that send it pay nothing for this. The call is capped and fails open:
        // no name is a cosmetic loss, a blocked sign-in is not. The access
        // token is used here and then dropped rather than stored.
        if (!idTokenHasDisplayName(idToken) && tokens.access_token) {
          const claims = await fetchStarfleetUserInfo({
            userInfoEndpoint: getStarfleetUserInfoEndpoint(loginOrigin),
            accessToken: tokens.access_token,
            // Binds the response to this token's subject; see the helper.
            idToken,
            signal: abort.signal,
          });
          if (cancelled) return;
          if (claims) writeStoredStarfleetUserInfo(claims);
        }

        clearOAuthTransientKeys();
        navigate('/', { replace: true });
      } catch (err) {
        if (cancelled || err?.name === 'AbortError') return;
        clearOAuthTransientKeys();
        setError(err?.message || 'Token exchange failed.');
      }
    }

    // run() handles the token-exchange failure itself; this guards the
    // pre-exchange setup (e.g. config load) so an unexpected throw surfaces an
    // error panel instead of spinning forever.
    run().catch((err) => {
      if (cancelled || err?.name === 'AbortError') return;
      setError(err?.message || 'Sign-in failed. Return to the app and try again.');
    });
    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [navigate, searchParams]);

  // Happy path (and while in flight): just the neutral spinner, matching
  // Home's silent-login / verification-preflight states so the round-trip
  // reads as one continuous load. Errors get a themed, readable panel instead
  // of the old bare black-on-dark text.
  if (!error) return <LoadingScreen label="Signing you in…" />;

  return (
    <div className="oauth-callback-error" role="alert">
      <p className="oauth-callback-error__message">{error}</p>
    </div>
  );
}
